import Core from '../Core/Core';
import { ILogger } from '../Core/Logger';
import getLogger from '../Core/Logger';
import * as Sentry from '@sentry/browser';
import { Storage } from '../Utils/Storage';

const sdkLogger: ILogger = getLogger('[SDK-DATA]:');

export interface ISDKdata {
    saveData: (key: string, value: any) => Promise<any>;
    getData: (key: string) => Promise<any>;
    loadSdkData: () => any; // should type loaded object, may help with validation
    saveAll: () => Promise<any>;
    saveUpdateNeeded: boolean;
}

export class SDKdata implements ISDKdata {
    public platform: any;
    public backend: any;
    public saveUpdateNeeded: boolean = false;
    public dangerSaving: boolean = false;

    constructor(platform: any, backend: any) {
        this.platform = platform;
        this.backend = backend;
    }

    public async saveData(key: string, value: any): Promise<any> {
        const stamped = {
            ts: Date.now(),
            value,
        };

        Storage.saveToStorage(key, stamped).catch(err => {
            console.log(['error with local savetostorage', err]);
        });

        this.platform.storeData(key, stamped).catch((err: any) => {
            console.log(['error with platform store data', err]);
        });

        if (await this.syncBackend()) {
            const start = Date.now();

            this.backend.storeData(key, stamped).catch((err: any) => {
                console.log(['error with backend store data', err]);
            });
            const end = Date.now();
            const diff = end - start;
            if (diff > 1000) {
                Sentry.captureMessage('store to GBE took longer than 1 second');
            }
        }

        //TODO handle errors from platform

        return;
    }

    public async getData(key: string): Promise<any> {
        let data = null;

        const fbData = await this.platform.retrieveData(key);
        let localData = null;
        await Storage.getFromStorage(key).then(res => {
            localData = JSON.parse(res);
        });

        /*
             let gbeData = null;
             if (await this.syncBackend()) {
                 const start = Date.now();

                 gbeData = await this.backend.retrieveData(key);

                 const end = Date.now();
                 const diff = end - start;
                 if (diff > 1000) {
                     Sentry.captureMessage('store to GBE took longer than 1 second');
                 }
             } */

        let allData = [localData, fbData];
        allData = allData.filter(d => d);
        if (allData.length > 1) {
            const entries = allData.filter(e => typeof e !== 'object' || Object.keys(e).length > 0);
            const objs = entries.filter(e => typeof e === 'object');
            // get all with timestamp
            let stamped = objs.filter(e => e.hasOwnProperty('ts'));
            if (stamped.length > 0) {
                stamped = stamped.sort((a, b) => a.ts - b.ts);
                data = stamped.pop().value;
            } else {
                if (entries.length > 0) {
                    data = entries[0];
                }
            }
        } else if (allData.length === 1) {
            const res = allData[0];
            if (typeof res === 'object') {
                if (res.hasOwnProperty('value')) {
                    data = res.value;
                } else {
                    data = res;
                }
            } else {
                data = res;
            }
        }

        return Promise.resolve(data);
    }

    public async loadSdkData() {
        this.dangerSaving = false;
        let dangerLoading = false;
        let gotSomeData = false;

        const loaded = {};
        const promises: Array<Promise<any>> = [];
        const self = this;

        // TODO change this...we are not actually waiting for each restore...it works due to the await of getPlayed below

        [
            'boosters',
            'activeCollection',
            'collectablesProgress',
            'coin',
            'heart',
            'mapId',
            'levelId',
            'totalStars',
            'lastLifeTime',
            'lastInterstitialDate',
            'isSoundOn',
            'isMusicOn',
            'unlockedMaps',
            'prizeWheelCooldown',
            'unlockedChests',
            'visitedMaps',
            'optInRewardDate',
            'optInShortcutRewardDate',
            'sharedGiftsCount',
            'sentCoins',
            'sentGifts',
            'requestedGifts',
            'messageIds',
            'tracked',
            'pendingInvites',
            'confirmedInvites',
            'receivedInvites',
            'missions',
            'missionRefreshTimer',
            'missionsCompleted',
            'lastLevelIndex',
            'lastLogin',
            'missionsShown',
            'claimedRewards',
            'purchases',
            'shopHasBeenOpened',
            'shopGift',
        ].forEach(key => {
            const load = self
                .getData(key)
                // @ts-ignore
                .then(res => {
                    if (res) {
                        gotSomeData = true;
                    }
                    // @ts-ignore
                    loaded[key] = res;
                })
                // @ts-ignore
                .catch(err => {
                    // will sdk throw error for all undefined if not connected
                    if (err.message === 'Saves failed for all storages. See errors property') {
                        dangerLoading = true;
                        Sentry.captureException(err);
                        Sentry.captureMessage(
                            'worst case restore scenario occurred for player: ' +
                                Core.sdk.user.userId
                        );
                    }
                    sdkLogger.debug('restore data error: ' + err + ', for: ' + key);
                });
            promises.push(load);
        });

        await Promise.all(promises);

        const played = {};
        await this.getPlayedLevels()
            .then(
                res => {
                    // @ts-ignore
                    played = { played: { ...res } };
                    console.log('PLAYER ID: ' + Core.sdk.user.userId);
                },
                () => {}
            )
            .catch(err => {
                if (err.message === '') sdkLogger.debug('error restoring levels: ' + err);
                Sentry.captureException(err);
                Sentry.captureMessage('error restoring levels: ' + err);
            });

        // keeping this for now...
        console.log('PLAYER LOADED DATA::::');
        console.dir({ ...loaded, ...played });

        if (!gotSomeData && dangerLoading) {
            this.dangerSaving = true;
        }

        return { ...loaded, ...played };
    }

    public async saveAll(): Promise<any> {
        let awaitAll: any[] = [];

        const boosters = this.saveData('boosters', [
            ...Core.store.getState().StateBoosters.availableBoosters,
        ]);
        awaitAll.push(boosters);

        const coin = this.saveData('coin', Core.store.getState().StateCurrency.coin);
        awaitAll.push(coin);

        const activeCollection = this.saveData(
            'activeCollection',
            Core.store.getState().StatePlayer.activeCollection
        );
        awaitAll.push(activeCollection);

        const collectablesProgress = this.saveData(
            'collectablesProgress',
            Core.store.getState().StatePlayer.collectablesProgress
        );
        awaitAll.push(collectablesProgress);

        const heart = this.saveData('heart', Core.store.getState().StateCurrency.heart);
        awaitAll.push(heart);

        const last = this.saveData('lastLifeTime', Core.store.getState().StateCurrency.ts);
        awaitAll.push(last);

        const lastInterstitialDate = this.saveData(
            'lastInterstitialDate',
            Core.store.getState().StatePlayer.lastInterstitialDate
        );
        awaitAll.push(lastInterstitialDate);

        const completed = this.saveData('completedLevels', {
            ...Core.store.getState().StatePlayer.mapLevels,
        });
        awaitAll.push(completed);

        const level = this.saveData('levelId', Core.store.getState().StatePlayer.levelId);
        awaitAll.push(level);

        const stars = this.saveData('totalStars', Core.store.getState().StatePlayer.totalStars);
        awaitAll.push(stars);

        const unlocked = this.saveData('unlockedMaps', Core.store.getState().StatePlayer.unlocked);
        awaitAll.push(unlocked);

        const unlockedChests = this.saveData(
            'unlockedChests',
            Core.store.getState().StatePlayer.unlockedChests
        );
        awaitAll.push(unlockedChests);

        const sound = this.saveData(
            'isSoundOn',
            Core.store.getState().StateSound.soundFx.toString()
        );
        awaitAll.push(sound);

        const music = this.saveData('isMusicOn', Core.store.getState().StateSound.music.toString());
        awaitAll.push(music);

        const visited = this.saveData('visitedMaps', Core.store.getState().StatePlayer.visited);
        awaitAll.push(visited);

        const optInData = this.saveData(
            'optInRewardDate',
            Core.store.getState().StateSocial.optInRewardDate
        );
        awaitAll.push(optInData);

        const optInShortcutData = this.saveData(
            'optInShortcutRewardDate',
            Core.store.getState().StatePlayer.optInShortcutRewardDate
        );
        awaitAll.push(optInShortcutData);

        const messagesReceived = this.saveData(
            'messageIds',
            Core.store.getState().StateSocial.messageIds
        );
        awaitAll.push(messagesReceived);

        const sharedGiftsCount = this.saveData(
            'sharedGiftsCount',
            Core.store.getState().StateSocial.sharedGiftsCount
        );
        awaitAll.push(sharedGiftsCount);

        const sentCoins = this.saveData('sentCoins', Core.store.getState().StateSocial.sentCoins);
        awaitAll.push(sentCoins);

        const sentGifts = this.saveData('sentGifts', Core.store.getState().StateSocial.sentGifts);
        awaitAll.push(sentGifts);

        const requestedGifts = this.saveData(
            'requestedGifts',
            Core.store.getState().StateSocial.requestedGifts
        );
        awaitAll.push(requestedGifts);

        const prizeWheelCooldown = this.saveData(
            'prizeWheelCooldown',
            Core.store.getState().StatePlayer.prizeWheelCooldown
        );
        awaitAll.push(prizeWheelCooldown);

        const missions = this.saveData('missions', Core.store.getState().StateMissions.missions);
        awaitAll.push(missions);

        const missionRefreshTimer = this.saveData(
            'missionRefreshTimer',
            Core.store.getState().StateMissions.missions
        );
        awaitAll.push(missionRefreshTimer);

        const missionsCompleted = this.saveData(
            'missionsCompleted',
            Core.store.getState().StateMissions.missions
        );
        awaitAll.push(missionsCompleted);

        const lastLevelIndex = this.saveData(
            'lastLevelIndex',
            Core.store.getState().StateMissions.missions
        );
        awaitAll.push(lastLevelIndex);

        const missionsShown = this.saveData(
            'missionsShown',
            Core.store.getState().StateMissions.missions
        );
        awaitAll.push(missionsShown);

        const claimedRewards = this.saveData(
            'claimedRewards',
            Core.store.getState().StateMissions.claimedRewards
        );
        awaitAll.push(claimedRewards);

        const tracked = this.saveData('tracked', Core.store.getState().StateTracking.tracked);
        awaitAll.push(tracked);

        await Promise.all(awaitAll)
            .then(() => {
                this.saveUpdateNeeded = false;
                awaitAll = [];
            })
            .catch(err => {
                awaitAll = [];
                sdkLogger.debug('error save update: ' + err);
                // should redo if connection is available?
            });

        return Promise.resolve('save all complete');
    }

    private async getPlayedLevels() {
        let played: any[] = [];
        await this.getData('completedLevels')
            .then(res => {
                played = res;
            })
            .catch(err => {
                sdkLogger.debug('error restoring completeLevels: ' + err);
            });
        return played;
    }

    private async syncBackend(): Promise<boolean> {
        return (
            this.backend && this.backend.isInitialized() && (await this.backend.checkConnection())
        );
    }
}
